import React, { useState, useEffect } from 'react';
import { Calendar, MapPin, Clock, Ticket, Users, ExternalLink, Send, X, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

// Glass card component
const GlassCard = ({ children, className = "" }) => (
  <div className={`backdrop-blur-xl bg-black/40 border border-white/10 rounded-2xl p-6 ${className}`}>
    {children}
  </div>
);

// Event Card Component
const EventCard = ({ event, onClick }) => {
  const formatDate = (dateStr) => {
    try {
      return new Date(dateStr).toLocaleDateString('en-US', { 
        weekday: 'short', 
        month: 'short', 
        day: 'numeric',
        year: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };

  return (
    <GlassCard className="cursor-pointer hover:border-cyan-500/50 transition-all duration-300 group">
      <div onClick={onClick} className="space-y-4">
        {event.image_url && (
          <div className="relative h-48 -mx-6 -mt-6 mb-4 overflow-hidden rounded-t-2xl">
            <img 
              src={event.image_url} 
              alt={event.title}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            />
            {event.featured && (
              <span className="absolute top-3 right-3 px-3 py-1 bg-cyan-500 text-black text-xs font-bold rounded-full">
                FEATURED
              </span>
            )}
            <span className="absolute bottom-3 left-3 px-3 py-1 bg-black/70 text-white text-xs font-medium rounded-full uppercase">
              {event.category}
            </span>
          </div>
        )}
        
        <div className="space-y-3">
          <h3 className="text-xl font-bold text-white group-hover:text-cyan-400 transition-colors">
            {event.title}
          </h3>
          
          <div className="flex items-center gap-2 text-cyan-400">
            <Calendar className="w-4 h-4" />
            <span className="text-sm">{formatDate(event.event_date)}</span>
            {event.event_time && (
              <>
                <Clock className="w-4 h-4 ml-2" />
                <span className="text-sm">{event.event_time}</span>
              </>
            )}
          </div>
          
          <div className="flex items-center gap-2 text-gray-400">
            <MapPin className="w-4 h-4" />
            <span className="text-sm">{event.venue || event.location}</span>
          </div>
          
          {event.artists && event.artists.length > 0 && (
            <div className="flex items-center gap-2 text-gray-400">
              <Users className="w-4 h-4" />
              <span className="text-sm">{event.artists.join(', ')}</span>
            </div>
          )}
          
          {event.ticket_price && (
            <div className="flex items-center gap-2 text-green-400">
              <Ticket className="w-4 h-4" />
              <span className="text-sm font-medium">{event.ticket_price}</span>
            </div>
          )}
        </div>
      </div>
    </GlassCard>
  );
};

// Event Submission Form
const SubmitEventForm = ({ onClose, onSuccess }) => {
  const [form, setForm] = useState({
    title: '',
    description: '',
    event_date: '',
    event_time: '',
    location: '',
    venue: '',
    address: '',
    city: '',
    state: '',
    image_url: '',
    ticket_url: '',
    ticket_price: '',
    category: 'concert',
    artists: '',
    facebook_event_url: '',
    instagram_url: '',
    contact_email: '',
    contact_phone: '',
    submitter_name: '',
    submitter_email: ''
  });
  const [submitting, setSubmitting] = useState(false);

  const categories = [
    { value: 'concert', label: 'Concert' },
    { value: 'festival', label: 'Festival' },
    { value: 'club-night', label: 'Club Night' },
    { value: 'listening-party', label: 'Listening Party' },
    { value: 'open-mic', label: 'Open Mic' },
    { value: 'workshop', label: 'Workshop' },
    { value: 'other', label: 'Other' }
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.event_date || !form.submitter_name || !form.submitter_email) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSubmitting(true);
    try {
      const submitData = {
        ...form,
        artists: form.artists ? form.artists.split(',').map(a => a.trim()) : []
      };
      await axios.post(`${API}/api/submit/event`, submitData);
      toast.success('Event submitted for review!');
      onSuccess?.();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit event');
    } finally {
      setSubmitting(false);
    }
  };

  const inputClass = "w-full h-12 px-4 rounded-lg bg-black/60 border-2 border-white/20 text-white placeholder:text-gray-500 focus:outline-none focus:border-cyan-500 transition-colors";
  const labelClass = "block text-sm font-medium mb-2 text-gray-300";

  return (
    <form onSubmit={handleSubmit} className="space-y-4 max-h-[70vh] overflow-y-auto pr-2">
      {/* Your Info */}
      <div className="p-4 rounded-lg bg-cyan-500/10 border border-cyan-500/30">
        <h3 className="font-bold text-cyan-400 mb-3">Your Information</h3>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className={labelClass}>Your Name *</label>
            <input
              type="text"
              value={form.submitter_name}
              onChange={(e) => setForm(f => ({...f, submitter_name: e.target.value}))}
              placeholder="Your name"
              className={inputClass}
              required
            />
          </div>
          <div>
            <label className={labelClass}>Your Email *</label>
            <input
              type="email"
              value={form.submitter_email}
              onChange={(e) => setForm(f => ({...f, submitter_email: e.target.value}))}
              placeholder="your@email.com"
              className={inputClass}
              required
            />
          </div>
        </div>
      </div>

      {/* Event Details */}
      <div>
        <label className={labelClass}>Event Title *</label>
        <input
          type="text"
          value={form.title}
          onChange={(e) => setForm(f => ({...f, title: e.target.value}))}
          placeholder="Event name"
          className={inputClass}
          required
        />
      </div>

      <div>
        <label className={labelClass}>Description</label>
        <textarea
          value={form.description}
          onChange={(e) => setForm(f => ({...f, description: e.target.value}))}
          placeholder="Tell us about the event..."
          className={`${inputClass} h-24 py-3`}
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className={labelClass}>Event Date *</label>
          <input
            type="date"
            value={form.event_date}
            onChange={(e) => setForm(f => ({...f, event_date: e.target.value}))}
            className={inputClass}
            required
          />
        </div>
        <div>
          <label className={labelClass}>Event Time</label>
          <input
            type="time"
            value={form.event_time}
            onChange={(e) => setForm(f => ({...f, event_time: e.target.value}))}
            className={inputClass}
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className={labelClass}>Category</label>
          <select
            value={form.category}
            onChange={(e) => setForm(f => ({...f, category: e.target.value}))}
            className={inputClass}
          >
            {categories.map(cat => (
              <option key={cat.value} value={cat.value} className="bg-black">{cat.label}</option>
            ))}
          </select>
        </div>
        <div>
          <label className={labelClass}>Ticket Price</label>
          <input
            type="text"
            value={form.ticket_price}
            onChange={(e) => setForm(f => ({...f, ticket_price: e.target.value}))}
            placeholder="$20 / Free"
            className={inputClass}
          />
        </div>
      </div>

      {/* Location */}
      <div className="p-4 rounded-lg bg-white/5 border border-white/10">
        <h3 className="font-bold text-white mb-3">Location</h3>
        <div className="space-y-4">
          <div>
            <label className={labelClass}>Venue Name</label>
            <input
              type="text"
              value={form.venue}
              onChange={(e) => setForm(f => ({...f, venue: e.target.value}))}
              placeholder="Club name, arena, etc."
              className={inputClass}
            />
          </div>
          <div>
            <label className={labelClass}>Address</label>
            <input
              type="text"
              value={form.address}
              onChange={(e) => setForm(f => ({...f, address: e.target.value}))}
              placeholder="Street address"
              className={inputClass}
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>City</label>
              <input
                type="text"
                value={form.city}
                onChange={(e) => setForm(f => ({...f, city: e.target.value}))}
                placeholder="City"
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass}>State</label>
              <input
                type="text"
                value={form.state}
                onChange={(e) => setForm(f => ({...f, state: e.target.value}))}
                placeholder="MS"
                className={inputClass}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Artists & Links */}
      <div>
        <label className={labelClass}>Featured Artists (comma separated)</label>
        <input
          type="text"
          value={form.artists}
          onChange={(e) => setForm(f => ({...f, artists: e.target.value}))}
          placeholder="DJ808, Artist Name, etc."
          className={inputClass}
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className={labelClass}>Event Image URL</label>
          <input
            type="url"
            value={form.image_url}
            onChange={(e) => setForm(f => ({...f, image_url: e.target.value}))}
            placeholder="https://..."
            className={inputClass}
          />
        </div>
        <div>
          <label className={labelClass}>Ticket URL</label>
          <input
            type="url"
            value={form.ticket_url}
            onChange={(e) => setForm(f => ({...f, ticket_url: e.target.value}))}
            placeholder="https://..."
            className={inputClass}
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className={labelClass}>Facebook Event URL</label>
          <input
            type="url"
            value={form.facebook_event_url}
            onChange={(e) => setForm(f => ({...f, facebook_event_url: e.target.value}))}
            placeholder="https://facebook.com/events/..."
            className={inputClass}
          />
        </div>
        <div>
          <label className={labelClass}>Instagram URL</label>
          <input
            type="url"
            value={form.instagram_url}
            onChange={(e) => setForm(f => ({...f, instagram_url: e.target.value}))}
            placeholder="https://instagram.com/..."
            className={inputClass}
          />
        </div>
      </div>

      <div className="flex gap-3 pt-4">
        <Button type="button" variant="outline" onClick={onClose} className="flex-1">
          Cancel
        </Button>
        <Button 
          type="submit" 
          disabled={submitting}
          className="flex-1 bg-cyan-500 hover:bg-cyan-400 text-black font-bold"
        >
          {submitting ? 'Submitting...' : 'Submit Event'}
        </Button>
      </div>
    </form>
  );
};

// Main Events Page
const Events = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showSubmitForm, setShowSubmitForm] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [submissionsEnabled, setSubmissionsEnabled] = useState(true);

  useEffect(() => {
    fetchEvents();
    fetchSubmissionsConfig();
  }, []);

  const fetchEvents = async () => {
    try {
      const res = await axios.get(`${API}/api/events?status=approved`);
      setEvents(res.data);
    } catch (error) {
      console.error('Failed to fetch events:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSubmissionsConfig = async () => {
    try {
      const res = await axios.get(`${API}/api/submissions/config`);
      setSubmissionsEnabled(res.data.events_submissions_enabled);
    } catch (error) {
      console.error('Failed to fetch submissions config:', error);
    }
  };

  const formatDate = (dateStr) => {
    try {
      return new Date(dateStr).toLocaleDateString('en-US', { 
        weekday: 'long',
        month: 'long', 
        day: 'numeric',
        year: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };

  // Group events by upcoming vs past
  const now = new Date();
  const upcomingEvents = events.filter(e => new Date(e.event_date) >= now);
  const pastEvents = events.filter(e => new Date(e.event_date) < now);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900 pt-4 pb-12 px-3 sm:px-4">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="text-center mb-6 sm:mb-8">
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-black mb-2">
            <span className="bg-gradient-to-r from-cyan-400 via-purple-500 to-pink-500 bg-clip-text text-transparent">
              EVENTS
            </span>
          </h1>
          <p className="text-gray-400 text-sm sm:text-base max-w-2xl mx-auto">
            Upcoming shows, concerts, and events in the Gulf Coast area and beyond
          </p>
          
          {submissionsEnabled && (
            <Button
              onClick={() => setShowSubmitForm(true)}
              className="mt-6 bg-gradient-to-r from-cyan-500 to-purple-500 hover:from-cyan-400 hover:to-purple-400 text-black font-bold px-8"
            >
              <Send className="w-4 h-4 mr-2" />
              Submit Your Event
            </Button>
          )}
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin w-8 h-8 border-2 border-cyan-500 border-t-transparent rounded-full mx-auto"></div>
          </div>
        ) : events.length === 0 ? (
          <GlassCard className="text-center py-12">
            <Calendar className="w-16 h-16 text-gray-600 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-white mb-2">No Events Yet</h3>
            <p className="text-gray-400 mb-6">Check back soon for upcoming shows and events!</p>
            {submissionsEnabled && (
              <Button
                onClick={() => setShowSubmitForm(true)}
                className="bg-cyan-500 hover:bg-cyan-400 text-black font-bold"
              >
                Submit the First Event
              </Button>
            )}
          </GlassCard>
        ) : (
          <div className="space-y-12">
            {/* Upcoming Events */}
            {upcomingEvents.length > 0 && (
              <div>
                <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-3">
                  <span className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></span>
                  Upcoming Events
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {upcomingEvents.map(event => (
                    <EventCard 
                      key={event.id} 
                      event={event} 
                      onClick={() => setSelectedEvent(event)}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Past Events */}
            {pastEvents.length > 0 && (
              <div>
                <h2 className="text-2xl font-bold text-gray-500 mb-6">Past Events</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 opacity-70">
                  {pastEvents.map(event => (
                    <EventCard 
                      key={event.id} 
                      event={event} 
                      onClick={() => setSelectedEvent(event)}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Submit Event Dialog */}
        <Dialog open={showSubmitForm} onOpenChange={setShowSubmitForm}>
          <DialogContent className="max-w-2xl bg-gray-900 border-white/10">
            <DialogHeader>
              <DialogTitle className="text-2xl font-bold text-cyan-400">Submit Your Event</DialogTitle>
            </DialogHeader>
            <SubmitEventForm 
              onClose={() => setShowSubmitForm(false)} 
              onSuccess={fetchEvents}
            />
          </DialogContent>
        </Dialog>

        {/* Event Detail Dialog */}
        <Dialog open={!!selectedEvent} onOpenChange={() => setSelectedEvent(null)}>
          <DialogContent className="max-w-2xl bg-gray-900 border-white/10">
            {selectedEvent && (
              <div className="space-y-6">
                {selectedEvent.image_url && (
                  <div className="relative h-64 -mx-6 -mt-6 overflow-hidden rounded-t-lg">
                    <img 
                      src={selectedEvent.image_url} 
                      alt={selectedEvent.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                )}
                
                <div>
                  <span className="px-3 py-1 bg-cyan-500/20 text-cyan-400 text-xs font-bold rounded-full uppercase">
                    {selectedEvent.category}
                  </span>
                  <h2 className="text-2xl font-bold text-white mt-3">{selectedEvent.title}</h2>
                </div>

                <div className="space-y-3">
                  <div className="flex items-center gap-3 text-cyan-400">
                    <Calendar className="w-5 h-5" />
                    <span>{formatDate(selectedEvent.event_date)}</span>
                    {selectedEvent.event_time && (
                      <>
                        <Clock className="w-5 h-5 ml-2" />
                        <span>{selectedEvent.event_time}</span>
                      </>
                    )}
                  </div>

                  <div className="flex items-center gap-3 text-gray-300">
                    <MapPin className="w-5 h-5" />
                    <div>
                      {selectedEvent.venue && <div className="font-medium">{selectedEvent.venue}</div>}
                      <div className="text-sm text-gray-500">
                        {[selectedEvent.address, selectedEvent.city, selectedEvent.state].filter(Boolean).join(', ')}
                      </div>
                    </div>
                  </div>

                  {selectedEvent.artists && selectedEvent.artists.length > 0 && (
                    <div className="flex items-center gap-3 text-gray-300">
                      <Users className="w-5 h-5" />
                      <span>{selectedEvent.artists.join(', ')}</span>
                    </div>
                  )}

                  {selectedEvent.ticket_price && (
                    <div className="flex items-center gap-3 text-green-400">
                      <Ticket className="w-5 h-5" />
                      <span className="font-bold">{selectedEvent.ticket_price}</span>
                    </div>
                  )}
                </div>

                {selectedEvent.description && (
                  <p className="text-gray-400 whitespace-pre-wrap">{selectedEvent.description}</p>
                )}

                <div className="flex gap-3 pt-4">
                  {selectedEvent.ticket_url && (
                    <Button
                      onClick={() => window.open(selectedEvent.ticket_url, '_blank')}
                      className="flex-1 bg-cyan-500 hover:bg-cyan-400 text-black font-bold"
                    >
                      <Ticket className="w-4 h-4 mr-2" />
                      Get Tickets
                    </Button>
                  )}
                  {selectedEvent.facebook_event_url && (
                    <Button
                      variant="outline"
                      onClick={() => window.open(selectedEvent.facebook_event_url, '_blank')}
                    >
                      <ExternalLink className="w-4 h-4 mr-2" />
                      Facebook
                    </Button>
                  )}
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default Events;
